/**
 * Token Cache Utility - Handles backend token caching and restoration.
 * Provides a 30-minute cache so users don't need to re-authenticate when navigating.
 */

const BACKEND_TOKEN_KEY = "backendToken";
const BACKEND_TOKEN_EXPIRES_KEY = "backendTokenExpires";
const BACKEND_TOKEN_CACHE_DURATION = 30 * 60 * 1000; // 30 minutes

/**
 * Get backend token from cache (localStorage).
 */
export function getBackendToken(): string | null {
  const cachedToken = localStorage.getItem(BACKEND_TOKEN_KEY);
  const cachedExpires = localStorage.getItem(BACKEND_TOKEN_EXPIRES_KEY);

  if (cachedToken && cachedExpires) {
    const expiresAt = parseInt(cachedExpires, 10);

    if (!isNaN(expiresAt) && Date.now() < expiresAt) {
      return cachedToken;
    }

    // Token expired - clear cache
    localStorage.removeItem(BACKEND_TOKEN_KEY);
    localStorage.removeItem(BACKEND_TOKEN_EXPIRES_KEY);
  }

  return null;
}

/**
 * Cache a backend token with expiration.
 * Parses the JWT exp claim and caps at BACKEND_TOKEN_CACHE_DURATION.
 */
export function cacheBackendToken(token: string, expiresInMs?: number): void {
  localStorage.setItem(BACKEND_TOKEN_KEY, token);

  if (expiresInMs) {
    localStorage.setItem(BACKEND_TOKEN_EXPIRES_KEY, (Date.now() + expiresInMs).toString());
    return;
  }

  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    const expiresAt = payload.exp
      ? Math.min(payload.exp * 1000, Date.now() + BACKEND_TOKEN_CACHE_DURATION)
      : Date.now() + BACKEND_TOKEN_CACHE_DURATION;
    localStorage.setItem(BACKEND_TOKEN_EXPIRES_KEY, expiresAt.toString());
  } catch {
    localStorage.setItem(
      BACKEND_TOKEN_EXPIRES_KEY,
      (Date.now() + BACKEND_TOKEN_CACHE_DURATION).toString(),
    );
  }
}

/**
 * Clear cached backend token.
 */
export function clearBackendTokenCache(): void {
  localStorage.removeItem(BACKEND_TOKEN_KEY);
  localStorage.removeItem(BACKEND_TOKEN_EXPIRES_KEY);
}

/**
 * Check if we have a valid cached token.
 */
export function hasValidCachedToken(): boolean {
  const cachedToken = localStorage.getItem(BACKEND_TOKEN_KEY);
  const cachedExpires = localStorage.getItem(BACKEND_TOKEN_EXPIRES_KEY);

  if (!cachedToken || !cachedExpires) return false;

  const expiresAt = parseInt(cachedExpires, 10);
  if (isNaN(expiresAt)) return false;

  return Date.now() < expiresAt;
}
